/**
 * Phase 30.6: WBS hierarchy operations.
 *
 * The schedule represents parent/child relationships entirely through the
 * WBS string: a task with WBS `1.2.3` is a child of the summary with WBS
 * `1.2`. There's no `parent_id` column. That has the virtue of simplicity
 * but means hierarchy edits boil down to careful string rewriting.
 *
 * This module implements three operations:
 *   - indentTask:    make a task a child of the row above it
 *   - outdentTask:   promote a task one level up
 *   - addSubtaskUnder: create a new task as a child of a summary
 *
 * Each operation returns a new tasks[] array. Successor recomputation,
 * cascade scheduling, and summary rollups are NOT performed here — those
 * are downstream concerns the store handles after applying the hierarchy
 * change.
 *
 * Critical behavior choices, per Phase 30.6 design discussion:
 *   1. Indenting under a Task auto-promotes it to Summary.
 *   2. Outdenting can leave the previous parent as an empty Summary —
 *      empty summaries are already a supported state (rollup leaves their
 *      dates alone).
 *   3. Predecessor references are rewritten so any task that referenced
 *      the moved task's OLD WBS is updated to its NEW WBS. This preserves
 *      dependency chains across structural edits.
 */

import type { Task } from '@/types';

// =============================================================================
// WBS utilities
// =============================================================================

/** Split a WBS string into numeric segments. "1.2.3" -> [1, 2, 3]. */
function parseWbs(wbs: string): number[] {
  if (!wbs) return [];
  return wbs
    .split('.')
    .map((s) => parseInt(s, 10))
    .filter((n) => Number.isFinite(n));
}

/** Render numeric segments back to a WBS string. */
function renderWbs(segments: number[]): string {
  return segments.join('.');
}

/** Depth of a WBS — `1` is depth 1, `1.2` is depth 2, etc. Empty -> 0. */
function wbsDepth(wbs: string): number {
  return parseWbs(wbs).length;
}

/** Find the highest existing trailing-segment under a parent prefix. */
function maxChildSuffix(allTasks: Task[], parentWbs: string): number {
  const prefix = parentWbs === '' ? '' : parentWbs + '.';
  const targetDepth = wbsDepth(parentWbs) + 1;
  let max = 0;
  for (const t of allTasks) {
    const w = (t.wbs || '').trim();
    if (!w) continue;
    if (parentWbs !== '' && !w.startsWith(prefix)) continue;
    if (wbsDepth(w) !== targetDepth) continue;
    const tail = parseWbs(w)[targetDepth - 1];
    if (tail > max) max = tail;
  }
  return max;
}

/** All tasks whose WBS is a descendant of `wbs` (proper descendants, not self). */
function descendantsOf(allTasks: Task[], wbs: string): Task[] {
  if (!wbs) return [];
  const prefix = wbs + '.';
  return allTasks.filter((t) => (t.wbs || '').startsWith(prefix));
}

// =============================================================================
// Predecessor reference rewriting
// =============================================================================

/**
 * Rewrite predecessor references on every task so that any reference to
 * `oldWbs` (or any descendant `oldWbs.X.Y`) is updated to the new prefix.
 *
 * A predecessor field is a comma-separated string like "1.2 FS+1d, 1.3 SS".
 * We tokenize, find the leading WBS portion of each token, remap if it
 * matches, and reassemble.
 */
function rewritePredecessorReferences(
  tasks: Task[],
  oldWbs: string,
  newWbs: string,
): Task[] {
  if (oldWbs === newWbs) return tasks;
  const oldPrefix = oldWbs + '.';
  const newPrefix = newWbs + '.';

  return tasks.map((t) => {
    const preds = (t.predecessors || '').trim();
    if (!preds) return t;

    const tokens = preds.split(',').map((s) => s.trim()).filter(Boolean);
    let changed = false;
    const remapped = tokens.map((tok) => {
      // Token shape: "<wbs>[ <type>[+/-<lag>]]" — first whitespace-delimited
      // chunk is the WBS reference.
      const match = tok.match(/^(\S+)(\s.*)?$/);
      if (!match) return tok;
      const refWbs = match[1];
      const rest = match[2] || '';
      let nextWbs = refWbs;
      if (refWbs === oldWbs) {
        nextWbs = newWbs;
      } else if (refWbs.startsWith(oldPrefix)) {
        nextWbs = newPrefix + refWbs.slice(oldPrefix.length);
      }
      if (nextWbs !== refWbs) changed = true;
      return nextWbs + rest;
    });

    if (!changed) return t;
    return { ...t, predecessors: remapped.join(', ') };
  });
}

// =============================================================================
// Public operations
// =============================================================================

export type HierarchyOpResult =
  | { ok: true; tasks: Task[] }
  | { ok: false; reason: string };

/**
 * Indent the task at `index` so it becomes a child of the row above it.
 *
 *  - Disabled (returns ok=false) when the task is the first row.
 *  - The row immediately above becomes the parent. If it's a Task, it's
 *    auto-promoted to Summary.
 *  - The target task's WBS becomes `<above.wbs>.<next-free-child-suffix>`.
 *  - Any descendants of the target task have their WBS rewritten under the
 *    new prefix so the subtree comes along intact.
 *  - Predecessor references on every other task that pointed at the old
 *    WBS (or any descendant of it) are updated to the new WBS.
 */
export function indentTask(tasks: Task[], index: number): HierarchyOpResult {
  if (index <= 0 || index >= tasks.length) {
    return { ok: false, reason: 'Cannot indent the first row.' };
  }
  const target = tasks[index];
  const above = tasks[index - 1];
  const oldWbs = (target.wbs || '').trim();
  const aboveWbs = (above.wbs || '').trim();
  if (!oldWbs || !aboveWbs) {
    return { ok: false, reason: 'Both rows need a WBS to indent.' };
  }

  // If the row above is already this task's parent, we can't indent further
  // without nesting deeper than the row above — which is the operation, but
  // we should sanity-check.
  // The new parent IS the row above; we always nest one level under it.
  const newSuffix = maxChildSuffix(tasks, aboveWbs) + 1;
  const newWbs = aboveWbs + '.' + newSuffix;

  if (newWbs === oldWbs) {
    return { ok: false, reason: 'Already at this level.' };
  }

  // Build mapping: for the target and all its descendants, remap WBS.
  const descendants = descendantsOf(tasks, oldWbs);
  const remap = new Map<string, string>(); // oldWbs -> newWbs
  remap.set(oldWbs, newWbs);
  for (const d of descendants) {
    const dOld = (d.wbs || '').trim();
    if (!dOld) continue;
    const dNew = newWbs + dOld.slice(oldWbs.length); // preserve the descendant tail
    remap.set(dOld, dNew);
  }

  // Apply WBS rewrites.
  let next = tasks.map((t) => {
    const w = (t.wbs || '').trim();
    if (!w) return t;
    const mapped = remap.get(w);
    if (!mapped) return t;
    return { ...t, wbs: mapped };
  });

  // Promote the row above to Summary if it isn't already (and isn't a milestone).
  if (above.type !== 'summary' && above.type !== 'milestone') {
    next = next.map((t) => (t.taskId === above.taskId ? { ...t, type: 'summary' as const } : t));
  }

  // Rewrite predecessor references for every remapped WBS.
  for (const [oldW, newW] of remap.entries()) {
    next = rewritePredecessorReferences(next, oldW, newW);
  }

  return { ok: true, tasks: next };
}

/**
 * Outdent the task at `index`: promote it one level up.
 *
 *  - Disabled (returns ok=false) when the task is already at root level.
 *  - The task's new WBS becomes the next available sibling of its CURRENT
 *    PARENT — i.e. moves up one level and slots in after the parent.
 *    Example: `2.3.1` -> `2.4` (next sibling of `2.3`).
 *  - Descendants of the target come along with the same prefix rewrite.
 *  - The emptied previous parent is left as a Summary (empty summary is a
 *    supported state — its rollup just leaves dates alone).
 *  - Predecessor references are rewritten.
 */
export function outdentTask(tasks: Task[], index: number): HierarchyOpResult {
  if (index < 0 || index >= tasks.length) {
    return { ok: false, reason: 'Invalid row.' };
  }
  const target = tasks[index];
  const oldWbs = (target.wbs || '').trim();
  const segments = parseWbs(oldWbs);
  if (segments.length <= 1) {
    return { ok: false, reason: 'Already at the top level.' };
  }

  // Parent is everything but the last segment.
  const parentSegments = segments.slice(0, -1);

  // Grandparent: where the task is going. New WBS = next sibling of parent.
  const grandparentSegments = parentSegments.slice(0, -1);
  const grandparentWbs = renderWbs(grandparentSegments);
  const parentTail = parentSegments[parentSegments.length - 1];

  // We want the new WBS to be the next number AFTER the parent at the
  // grandparent's level. We can't just do parent_tail + 1 because that
  // might collide; find the highest sibling >= parent_tail and add 1.
  let maxAtLevel = parentTail;
  const targetDepth = parentSegments.length;
  const grandprefix = grandparentWbs === '' ? '' : grandparentWbs + '.';
  for (const t of tasks) {
    const w = (t.wbs || '').trim();
    if (!w) continue;
    if (grandparentWbs !== '' && !w.startsWith(grandprefix)) continue;
    if (wbsDepth(w) !== targetDepth) continue;
    const tail = parseWbs(w)[targetDepth - 1];
    if (tail > maxAtLevel) maxAtLevel = tail;
  }
  const newTail = maxAtLevel + 1;
  const newWbs =
    grandparentWbs === '' ? String(newTail) : grandparentWbs + '.' + newTail;

  // Build remap for target and descendants.
  const descendants = descendantsOf(tasks, oldWbs);
  const remap = new Map<string, string>();
  remap.set(oldWbs, newWbs);
  for (const d of descendants) {
    const dOld = (d.wbs || '').trim();
    if (!dOld) continue;
    const dNew = newWbs + dOld.slice(oldWbs.length);
    remap.set(dOld, dNew);
  }

  // Apply WBS rewrites.
  let next = tasks.map((t) => {
    const w = (t.wbs || '').trim();
    if (!w) return t;
    const mapped = remap.get(w);
    if (!mapped) return t;
    return { ...t, wbs: mapped };
  });

  // Rewrite predecessor references for every remapped WBS.
  for (const [oldW, newW] of remap.entries()) {
    next = rewritePredecessorReferences(next, oldW, newW);
  }

  return { ok: true, tasks: next };
}

/**
 * Compute the WBS for a new subtask under `parentWbs`.
 * Used by the store's addSubtaskUnder action.
 */
export function nextChildWbs(allTasks: Task[], parentWbs: string): string {
  const next = maxChildSuffix(allTasks, parentWbs) + 1;
  return parentWbs === '' ? String(next) : parentWbs + '.' + next;
}
